import type { WorkHours } from './types'

const env = import.meta.env

const readNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const readFlag = (value: unknown) => String(value ?? '').trim().toLowerCase() === 'true'

const readString = (value: unknown) => String(value ?? '').trim()

export const realtimeConfig = {
  /** Demo mode: fake sockets + sample data, no backend needed. */
  useMock: readFlag(env.VITE_USE_MOCK_REALTIME),
  /** WebSocket endpoint for push events. Empty = polling only. */
  wsUrl: readString(env.VITE_REALTIME_WS_URL),
  /** Job/Realtime poll (the only poll), used while no WebSocket is connected. */
  pollIntervalMs: readNumber(env.VITE_REALTIME_POLL_MS, 30_000),
  /** Google Cloud map id. Enables AdvancedMarkerElement when present. */
  mapId: readString(env.VITE_GOOGLE_MAPS_MAP_ID),
  /** Use Google Directions for the selected job's route (needs the Directions API on the key). */
  useDirections: readFlag(env.VITE_REALTIME_USE_DIRECTIONS),
  /** Use Google Distance Matrix for candidate ETAs (needs the Distance Matrix API on the key). */
  useDistanceMatrix: readFlag(env.VITE_REALTIME_USE_DISTANCE_MATRIX),
  /** Probability (0-1) that a mock add / remove / candidates request fails (rollback + error states). */
  mockAssignFailureRate: Math.min(Math.max(Number(env.VITE_MOCK_ASSIGN_FAILURE_RATE) || 0, 0), 1),
  workHours: {
    startHour: readNumber(env.VITE_REALTIME_WORK_START_HOUR, 8),
    endHour: readNumber(env.VITE_REALTIME_WORK_END_HOUR, 18),
  } satisfies WorkHours,
} as const

/**
 * How long each dataset of the Real Time board may be reused before it is fetched again.
 * Only Job/Realtime is polled; the rest refresh when Job/Realtime shows a change, when their
 * max age runs out, on tab return / reconnect, or immediately on Refresh / Retry.
 */
export const REFRESH_POLICY = {
  /** JobAssignment/List and JobWorkDayEntry/List for the day (staffing, check-ins). */
  staffingMaxAgeMs: 2 * 60_000,
  /** Worker/List and Job/List for the day (names, phones, addresses, categories). */
  referenceMaxAgeMs: 5 * 60_000,
  /** Per-job details of overdue jobs (their slots + Job/Get), re-fetched sooner if their open slots change. */
  overdueDetailsTtlMs: 10 * 60_000,
  /** Job/Get for day jobs missing from the Job/List page (address / coordinates). */
  jobDetailsTtlMs: 5 * 60_000,
  /** Per-job background lookups run at most this many at a time. */
  detailConcurrency: 2,
} as const

/** Per-worker location throttle window. */
export const LOCATION_THROTTLE_MS = 2_000
/** Data is considered stale after this long without an event or poll. */
export const STALE_AFTER_MS = 30_000
/** Consecutive failed polls before the board is reported offline. */
export const OFFLINE_AFTER_FAILED_POLLS = 3
/** How long a changed job card stays highlighted. */
export const HIGHLIGHT_MS = 2_400
/** Virtualize the jobs list above this many items. */
export const VIRTUALIZE_JOBS_ABOVE = 100
/** Average urban travel speed used for ETA estimates. */
export const ETA_SPEED_KMH = 25
